import { Link } from "react-router-dom";
import { guEvents } from "@/mocks/events";

const formatHeat = (value: number) =>
  value >= 10000 ? `${(value / 10000).toFixed(1)}万` : `${value}`;

export default function Hero() {
  const featured = guEvents[0];
  const ranking = guEvents.slice(0, 5);

  return (
    <section className="border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold tracking-wide text-primary-700">
              <i className="ri-eye-line text-sm" />
              今日瓜田 · 评论区正在更新
            </span>
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
              今天评论区
              <span className="text-primary-600">又为哪场瓜沦陷了？</span>
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              瓜田观察只做一件事：把网红吃瓜事件的评论区风向摊开给你看。谁在翻车、谁在洗白、路人到底怎么评价，一页看完再决定要不要吃这口瓜。
            </p>
          </div>
          <Link
            to="/topics"
            className="inline-flex w-fit shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-950 px-5 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
          >
            浏览全部专题
            <i className="ri-arrow-right-line text-lg" />
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <article className="group relative overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
            <div className="relative h-[240px] w-full overflow-hidden md:h-[340px]">
              <img
                src={featured.cover}
                alt={`${featured.title} - 网红吃瓜评论区风向配图`}
                title={`${featured.title} 评论区热议`}
                className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                    {featured.categoryLabel}
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-background-50/90 px-3 py-1 text-xs font-semibold text-foreground-800">
                    <i className="ri-fire-line text-sm text-primary-600" />
                    热度 {formatHeat(featured.heat)}
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-background-50/90 px-3 py-1 text-xs font-semibold text-foreground-800">
                    <i className="ri-chat-3-line text-sm text-secondary-600" />
                    {formatHeat(featured.commentCount)} 条评论
                  </span>
                </div>
                <h2 className="font-heading text-xl font-black leading-snug text-background-50 md:text-2xl">
                  {featured.title}
                </h2>
                <p className="mt-2 line-clamp-2 max-w-2xl text-sm leading-relaxed text-background-200">
                  {featured.summary}
                </p>
                <Link
                  to={`/event/${featured.id}`}
                  className="mt-4 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-300"
                >
                  查看评论区现场
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </article>

          <aside className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-black text-foreground-950">吃瓜热榜</h2>
              <span className="text-xs font-medium text-foreground-500">按讨论热度排序</span>
            </div>
            <ol className="mt-4 flex flex-col">
              {ranking.map((item, index) => (
                <li key={item.id} className="border-b border-background-200 last:border-b-0">
                  <Link
                    to={`/event/${item.id}`}
                    className="group flex cursor-pointer items-start gap-3 py-3.5"
                  >
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-black ${
                        index === 0
                          ? "bg-primary-500 text-background-50"
                          : index === 1
                            ? "bg-accent-500 text-foreground-950"
                            : "bg-background-200 text-foreground-700"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-1 flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-fire-line text-primary-500" />
                          {formatHeat(item.heat)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line text-secondary-500" />
                          {formatHeat(item.commentCount)}
                        </span>
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </aside>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { icon: "ri-chat-smile-3-line", label: "收录评论", value: "19.4 万条" },
            { icon: "ri-question-answer-line", label: "神评翻车", value: "1,286 条" },
            { icon: "ri-radar-line", label: "风向统计", value: "每 6 小时更新" },
            { icon: "ri-shield-check-line", label: "内容原则", value: "只整理 不站队" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-4"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className={`${stat.icon} text-xl`} />
              </span>
              <span className="min-w-0">
                <span className="block text-xs text-foreground-500">{stat.label}</span>
                <span className="block text-sm font-bold text-foreground-950">{stat.value}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}